import { useContext, useEffect, useState } from "react";
import UserContext from "@contexts/UserContext";
import momentTz from "moment-timezone";
import * as _ from "lodash-es";
import UIService from "@services/UIService";
import UserService from "@services/UserService";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { Form, Formik } from "formik";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwButton from "@components/shared/OwButton/OwButton";
import { SocketContext } from "@contexts/SocketContext";
import EndpointsEnum from "@models/EndpointsEnum";
import IUpdateUserSettingsDTO from "@models/DTO/IUpdateUserSettingsDTO";
import ISaveUserPreferencesDTO from "@models/DTO/ISaveUserPreferencesDTO";
import IUiSocketSetup from "@models/IUiSocketSetup";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import UiContext from "@contexts/UiContext";
import { OwFormikField } from "@components/shared/OwFormikField/OwFormikField";

type Props = {};

export const ProfilePreferences = (props: Props) => {
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [isSaving, setIsSaving] = useState(false);
    const [prefTimezone, setPrefTimezone] = useState(userModel.pref_timezone || undefined);
    const [phone, setPhone] = useState(userModel.phone || "");
    const [cell, setCell] = useState(userModel.cell || "");

    const onSavePreferences = (values: ISaveUserPreferencesDTO) => {
        setIsSaving(false);

        const notification: INotification = {
            message: "successfully_saved_preferences",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.ProfileOnSaveUserPreferences, fn: onSavePreferences }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const getTimezoneOptions = () => {
        const mTz = momentTz();

        const timezones = momentTz.tz.names();

        const processedTimezones = timezones.map((zone) => {
            return { name: zone };
        });

        return processedTimezones;
    };

    const timezoneOptions = getTimezoneOptions();
    const timezonePref = _.get(userModel, "pref_timezone", undefined);

    const handleChange = (e: any) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "pref_timezone":
                setPrefTimezone(value);
                break;

            case "phone":
                setPhone(value);
                break;

            case "cell":
                setCell(value);
                break;
        }
    };

    let cardClass = "card";

    if (!UIService.isMainViewLight()) {
        cardClass += " bg-dark text-white";
    }

    const preferencesInitialValues = {
        timezone: prefTimezone,
        phone,
        cell,
    };

    const preferencesSchema = UserService.getPreferencesSchema();

    const handlePreferencesSubmit = (
        values: typeof preferencesInitialValues,
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
        // Simulate an API call
        // setTimeout(() => {
        //     console.log(values);
        //     setSubmitting(false);
        //     alert('Preferences changed successfully! (Simulated)'); // Replace with actual success message
        // }, 2000);

        const payload: ISaveUserPreferencesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            pref_timezone: values.timezone,
            cell: values.cell,
            phone: values.phone,
        };

        socket.emit(EndpointsEnum.ProfileSaveUserPreferences, payload);

        setIsSaving(true);
    };

    return (
        <div className={cardClass + " h-100"}>
            <div className="card-header">
                <OwTitle title={"preferences"} />
            </div>
            <div className="card-body">
                <Formik initialValues={preferencesInitialValues} validationSchema={preferencesSchema} onSubmit={handlePreferencesSubmit}>
                    {({ isSubmitting, isValid, values }) => {
                        return (
                            <Form className="space-y-6">
                                <div>
                                    <OwLabel title={"timezone"} />
                                    <OwSelectOption
                                        options={timezoneOptions}
                                        valueProp={"name"}
                                        titleProp={"name"}
                                        fn={handleChange}
                                        name={"pref_timezone"}
                                        selectedOption={timezonePref}
                                        hasSelectedOptionProp={false}
                                        showBlankFirst={true}
                                    />
                                </div>
                                <div className="mt-4">
                                    <OwFormikField name={"phone"} label={"phone"} />
                                </div>
                                <div className="mt-4">
                                    <OwFormikField name={"cell"} label={"cell"} />
                                </div>
                                <div className="mt-4">
                                    <OwButton title={"save"} disabled={!isValid} type={"submit"} active={isSaving} disableOnActive={true} />
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
};
